import React, { Fragment, useCallback, useState } from 'react';
import { flattenDeep, get } from 'lodash';
import { messageFormat } from '@clubmed/utils';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import {
  ICONS as MEDIA_ICONS,
  MEDIA_TYPES,
  ORDERED_MEDIAS,
  ORDERED_MEDIAS_LAUNCHER,
} from 'Components/utils/medias/medias';
import { ICONS } from 'Components/utils/icon/icons';
import { ActionButtonsGroup } from 'Components/frameworks/action-buttons-group/ActionButtonsGroup';
import { Zoom } from 'Components/frameworks/zoom/Zoom';
import { Backdrop } from 'Components/frameworks/backdrop/Backdrop';
import { Carousel } from 'Components/frameworks/carousel/Carousel';
import { Image } from 'Components/frameworks/image/Image';
import { Video } from 'Components/frameworks/video/Video';
import { MultimediaLauncher } from 'Components/widgets/multimedia-launcher/MultimediaLauncher';
import { StreetView as StreetViewComponent } from 'Components/frameworks/map/StreetView';
export type MultimediaResources = {
  photos?: Array<{
    url: string;
  }>;
  streetViews?: Array<StreetView>;
  floorPlans?: Array<{
    url: string;
  }>;
  videos?: Array<{
    thumbnailUrl: string;
    url: string;
  }>;
  videos360?: Array<{
    thumbnailUrl: string;
    url: string;
  }>;
};
export type MultimediaLabels = {
  floorPlans?: string;
  photos?: string;
  streetViews?: string;
  videos?: string;
  videos360?: string;
};
export type MultimediaProps = {
  className?: string;
  closeLabel?: string;
  descriptionLabels?: {
    photos?: string;
    streetViews?: string;
    videos?: string;
    videos360?: string;
    floorPlans?: string;
  };
  galleryLabel?: string;
  hasFullScreenButton?: boolean;
  hasImageSlideShowLauncher?: boolean;
  isExclusiveCollection?: boolean;
  isOverlay?: boolean;
  initialIndex?: number;
  labels?: MultimediaLabels;
  launcherPictureAltLabel?: string;
  medias: MultimediaResources;
  nextLabel?: string;
  onClickMediaBtn?: (type: string) => void;
  openGalleryLabel?: string;
  pictureAltLabel?: string;
  portalName?: string;
  previousLabel?: string;
  ratioKey: string;
  theme?: string;
};
const DIMENSIONS = {
  w: 'auto',
  h: 'auto',
};

function _Multimedia({
  className,
  closeLabel,
  descriptionLabels,
  galleryLabel,
  hasFullScreenButton = false,
  hasImageSlideShowLauncher = false,
  isExclusiveCollection,
  isOverlay,
  initialIndex,
  labels = {},
  launcherPictureAltLabel,
  medias = {},
  nextLabel,
  onClickMediaBtn,
  openGalleryLabel,
  pictureAltLabel = '',
  portalName,
  previousLabel,
  ratioKey = ratioUtils.L,
  theme,
}: MultimediaProps) {
  const { isDesktop } = useResponsive();
  const [activeMedias, setActiveMedias] = useState('');
  const [carouselIndex, setCarouselIndex] = useState(0);

  const closeCarousel = useCallback(() => {
    setActiveMedias('');
    setCarouselIndex(0 || initialIndex);
  }, [initialIndex]);

  const showCarousel = useCallback((type: string, index: number = 0) => {
    setActiveMedias(type);
    setCarouselIndex(index);
  }, []);

  /* Carousel */
  // eslint-disable-next-line max-params, clubmed/no-fake-component
  function getCarouselItem(
    type: string,
    { fov, heading, latitude, longitude, url }: any,
    altMsg: string,
    isDesktop: boolean,
  ) {
    switch (type) {
      case MEDIA_TYPES.PHOTOS:
        return (
          <div
            data-testid="MultimediaPhoto"
            className="relative flex h-full w-full max-w-full flex-1 items-center justify-center overflow-hidden"
          >
            <Image
              altMsg={altMsg}
              classNameImg="absolute top-1/2 left-1/2 max-w-[2000px] transform -translate-x-1/2 -translate-y-1/2"
              hasLazyLoading={false}
              key={`CarouselItem-${type}-${url}`}
              imageUrl={url}
              dimensions={DIMENSIONS}
              fullscreen
              transformation="resize"
            />
          </div>
        );

      case MEDIA_TYPES.STREETVIEWS:
        if (url?.includes('matterport')) {
          return (
            <iframe
              title="matterport iframe"
              width="100%"
              height="100%"
              src={`${url}&play=1`}
              frameBorder="0"
              id="matterport-iframe"
            />
          );
        }

        return (
          <StreetViewComponent
            fov={fov}
            heading={heading}
            key={`CarouselItem-${type}-${latitude}-${longitude}`}
            latitude={latitude}
            longitude={longitude}
          />
        );

      case MEDIA_TYPES.FLOORPLAN:
        return isDesktop ? (
          <div className="h-full pt-20">
            <div
              data-testid="MultimediaFloorplan"
              className="relative flex h-full w-full max-w-full flex-1 items-center justify-center overflow-hidden"
            >
              <Image
                altMsg={altMsg}
                classNameImg="absolute top-1/2 left-1/2 max-w-[2000px] transform -translate-x-1/2 -translate-y-1/2"
                hasLazyLoading={false}
                key={`CarouselItem-${type}-${url}`}
                imageUrl={url}
                dimensions={DIMENSIONS}
                fullscreen
                transformation="resize"
              />
            </div>
          </div>
        ) : (
          <div className="flex h-full max-w-screen flex-col justify-center">
            <Zoom imageUrl={url} altMsg="Floor plan" inert />
          </div>
        );

      case MEDIA_TYPES.VIDEOS:
      case MEDIA_TYPES.VIDEOS360:
        return (
          <Video
            className="static m-auto flex h-full flex-1 items-center justify-center overflow-hidden md:w-4/5 md:max-w-4/5"
            key={`CarouselItem-${type}-${url}`}
            url={url}
          />
        );

      default:
        return null;
    }
  }

  function getCarouselItems(type: string, isDesktop: boolean) {
    return (medias[type] || []).map((media, idx) => {
      const altMsg =
        media?.alt ||
        messageFormat(pictureAltLabel).format({
          nb: idx + 1,
          total: medias[type].length,
        });
      const carouselItem = getCarouselItem(type, media, altMsg, isDesktop);
      return <Fragment key={`CarouselItemWrapper-${type}-${media.url}`}>{carouselItem}</Fragment>;
    });
  }

  /* Medias */
  function hasMedias(): boolean {
    return Object.keys(medias).some((type) => hasTypeMedias(type, isDesktop));
  }

  function hasTypeMedias(type: string, isDesktopParam: boolean): boolean {
    const has = Array.isArray(medias[type]) && medias[type].length > 0;

    if (type === MEDIA_TYPES.VIDEOS360) {
      return has && isDesktopParam;
    }

    return has;
  }

  function mapLauncherMedias(type: string, isDesktop: boolean): Array<any> {
    const launcherMedias = hasTypeMedias(type, isDesktop) ? medias[type] : [];
    return launcherMedias.map((media, index) => {
      return {
        alt: messageFormat(launcherPictureAltLabel || '').format({
          nb: index + 1,
          mediaType: labels && labels[type],
        }),
        hasForcedImage: !!media.thumbnailUrl,
        iconName: type === MEDIA_TYPES.PHOTOS ? '' : MEDIA_ICONS[type],
        onClick: () => {
          onClickMediaBtn?.(type);
          showCarousel(type, index);
        },
        ratio: ratioKey,
        src: media.thumbnailUrl || media.url || media.imageLarge || '',
      };
    });
  }

  function getLauncherMedias(
    isDesktop: boolean,
    hasImageSlideShowLauncherParam?: boolean,
  ): Array<any> {
    const types = hasImageSlideShowLauncherParam ? [MEDIA_TYPES.PHOTOS] : ORDERED_MEDIAS_LAUNCHER;
    return flattenDeep(
      types.map((type) => {
        return mapLauncherMedias(type, isDesktop);
      }),
    ).filter((i) => !!i.src);
  }

  function getLauncherProps(isDesktop: boolean) {
    const hasPhotosTypeMedias = hasTypeMedias(MEDIA_TYPES.PHOTOS, isDesktop);

    const getIndexToOpenTo = (type, index) => {
      if (type !== MEDIA_TYPES.PHOTOS) {
        return index;
      }

      return initialIndex || index;
    };

    const getButton = (type, index) => {
      const numberOfItems =
        Array.isArray(medias?.[type]) && type !== MEDIA_TYPES.FLOORPLAN
          ? medias[type].length
          : null;
      return {
        ariaLabel: openGalleryLabel
          ? `${labels[type]} (${get(medias, [type], []).length}) - ${openGalleryLabel}`
          : null,
        dataTestId: `Multimedia-${type}-button`,
        iconIsNotSquare: type === MEDIA_TYPES.STREETVIEWS,
        iconName: MEDIA_ICONS[type],
        id: type,
        isActive: type === activeMedias,
        label: labels[type],
        number: numberOfItems,
        onClick: () => {
          onClickMediaBtn?.(type);
          showCarousel(type, getIndexToOpenTo(type, index));
        },
      };
    };

    const buttons = ORDERED_MEDIAS.map((type, index) => {
      const internalIndex = type === MEDIA_TYPES.PHOTOS ? index : 0;
      return hasTypeMedias(type, isDesktop) ? getButton(type, internalIndex) : null;
    }).filter(Boolean);
    const extraButtons = [
      hasFullScreenButton
        ? {
            ...getButton(MEDIA_TYPES.PHOTOS),
            iconName: ICONS.EXPAND,
            label: '',
            minWidth: 'initial',
            buttonClassName:
              'absolute end-0 md:bottom-0 md:top-auto top-0 mt-10 md:mx-10 md:mx-20 mb-0',
          }
        : null,
    ].filter(Boolean);
    return {
      buttons,
      extraButtons,
      medias:
        hasImageSlideShowLauncher && hasPhotosTypeMedias
          ? getLauncherMedias(isDesktop, hasImageSlideShowLauncher)
          : getLauncherMedias(isDesktop).slice(0, 5),
    };
  }

  if (!hasMedias()) {
    return null;
  }

  const descriptionLabel = descriptionLabels?.[activeMedias];
  const items = getCarouselItems(activeMedias, isDesktop);
  const launcherProps = getLauncherProps(isDesktop);

  return (
    <Fragment>
      <MultimediaLauncher
        buttons={[...launcherProps.buttons, ...launcherProps.extraButtons]}
        className={className}
        closeLabel={closeLabel}
        galleryLabel={galleryLabel}
        isExclusiveCollection={isExclusiveCollection}
        hasLazyLoading
        isImageSlideShow={hasImageSlideShowLauncher}
        isOverlay={isOverlay}
        medias={launcherProps.medias}
        nextLabel={nextLabel}
        previousLabel={previousLabel}
        theme={theme}
      />
      {!!activeMedias && (
        <Backdrop
          ariaLabel={galleryLabel}
          className="backdrop-blur-[4px] backdrop-saturate-0"
          closeLabel={closeLabel}
          hasCloseButton
          onClose={closeCarousel}
          portalName={portalName}
        >
          <div className="flex h-full w-full flex-col">
            <Carousel
              className="flex flex-1"
              hasInfiniteScroll
              initialIndex={carouselIndex}
              key={activeMedias}
              nextLabel={nextLabel}
              previousLabel={previousLabel}
            >
              {items}
            </Carousel>
            {descriptionLabel && (
              <div className="flex w-full justify-center p-20 py-10">
                <span className="text-center text-12 text-white">{descriptionLabel}</span>
              </div>
            )}
            <div className="flex h-[72px] items-center justify-center px-10">
              <ActionButtonsGroup
                isScrollable
                items={launcherProps.buttons}
                minWidth="6.25rem"
                reverseLabelAndNumber={theme !== 'accommodation'}
                theme="overlay"
              />
            </div>
          </div>
        </Backdrop>
      )}
    </Fragment>
  );
}

export const Multimedia = _Multimedia;
